import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  Palmtree,
  CalendarCheck2,
  Clock,
  CreditCard,
  Send,
  CheckCircle2,
  Megaphone,
  Check,
} from "lucide-react";
import { get } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

function fmtTime(dt) {
  if (!dt) return "—";
  const d = typeof dt === "string" ? new Date(dt.replace(" ", "T")) : new Date(dt);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getWorkingDays(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let count = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month, d).getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
}

function isLateCheckIn(a) {
  if (!a?.check_in) return false;
  const d = new Date(a.check_in);
  return d.getHours() * 60 + d.getMinutes() > 8 * 60 + 30;
}

function buildCalendar(attendances, year, month) {
  const now = new Date();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
  const rows = [];
  let dayCells = [];
  for (let i = 0; i < startOffset; i++) dayCells.push({ type: "empty" });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const dow = new Date(year, month, d).getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isToday = d === now.getDate() && month === now.getMonth() && year === now.getFullYear();
    const isFuture = new Date(year, month, d) > new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayAtts = attendances.filter((a) => {
      const checkInDate = a.check_in ? a.check_in.split(" ")[0] : "";
      return checkInDate === dateStr;
    });
    const wasPresent = dayAtts.length > 0;
    const wasLate = wasPresent && dayAtts.some(isLateCheckIn);
    if (isWeekend) {
      dayCells.push({ type: "weekend", day: d });
    } else if (wasLate) {
      dayCells.push({ type: "late", isToday, day: d });
    } else if (wasPresent) {
      dayCells.push({ type: "present", isToday, day: d });
    } else if (isToday) {
      dayCells.push({ type: "today", day: d });
    } else if (isFuture) {
      dayCells.push({ type: "future", day: d });
    } else {
      dayCells.push({ type: "absent", day: d });
    }
    if (dayCells.length === 7) {
      rows.push(dayCells);
      dayCells = [];
    }
  }
  if (dayCells.length > 0) {
    while (dayCells.length < 7) dayCells.push({ type: "empty" });
    rows.push(dayCells);
  }
  return rows;
}

const ESSDashboardPage = () => {
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const uid = user?.email;
  const now = useMemo(() => new Date(), []);
  const CUR_YEAR = now.getFullYear();
  const MONTH_KEYS = [
    "ess.dashboard.monthJan", "ess.dashboard.monthFeb", "ess.dashboard.monthMar",
    "ess.dashboard.monthApr", "ess.dashboard.monthMay", "ess.dashboard.monthJun",
    "ess.dashboard.monthJul", "ess.dashboard.monthAug", "ess.dashboard.monthSep",
    "ess.dashboard.monthOct", "ess.dashboard.monthNov", "ess.dashboard.monthDec",
  ];
  const WEEKDAY_KEYS = [
    "ess.dashboard.weekdayMon", "ess.dashboard.weekdayTue", "ess.dashboard.weekdayWed",
    "ess.dashboard.weekdayThu", "ess.dashboard.weekdayFri", "ess.dashboard.weekdaySat",
    "ess.dashboard.weekdaySun",
  ];
  const CUR_MONTH = intl.formatMessage({ id: MONTH_KEYS[now.getMonth()] });

  const statsFallback = useMemo(
    () => [
      { labelId: "ess.dashboard.leaveBalance", value: "—", sub: intl.formatMessage({ id: "ess.common.noData" }), icon: Palmtree, bg: "bg-emerald-50/50 border-emerald-100", iconBg: "bg-emerald-500" },
      { labelId: "ess.dashboard.attendance", value: "—", sub: intl.formatMessage({ id: "ess.common.noData" }), icon: CalendarCheck2, bg: "bg-green-50/50 border-green-100", iconBg: "bg-green-500" },
      { labelId: "ess.dashboard.pending", value: "0", sub: intl.formatMessage({ id: "ess.dashboard.noAction" }), icon: Clock, bg: "bg-amber-50/50 border-amber-100", iconBg: "bg-amber-500" },
      { labelId: "ess.dashboard.nextPayslip", value: "—", sub: intl.formatMessage({ id: "ess.common.noData" }), icon: CreditCard, bg: "bg-blue-50/50 border-blue-100", iconBg: "bg-blue-500" },
    ],
    [intl]
  );

  const announcementsFallback = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: "ess.dashboard.welcomeTitle" }),
        sub: intl.formatMessage({ id: "ess.dashboard.welcomeSub" }),
        date: new Date().toLocaleDateString(intl.locale),
      },
      {
        title: intl.formatMessage({ id: "ess.dashboard.dataPendingTitle" }),
        sub: intl.formatMessage({ id: "ess.dashboard.dataPendingSub" }),
        date: "",
      },
    ],
    [intl]
  );

  const [stats, setStats] = useState(statsFallback);
  const [summary, setSummary] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [announcements, setAnnouncements] = useState(announcementsFallback);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setStats(statsFallback);
  }, [statsFallback]);

  const loadData = useCallback(async () => {
    try {
      const [employeeRes, deptRes] = await Promise.all([
        get("/api/employees?limit=500"),
        get("/api/departments"),
      ]);
      const allEmployees = employeeRes?.data || [];
      const deptRows = deptRes?.data || [];
      const deptMap = {};
      deptRows.forEach((d) => { deptMap[d.id] = d.name || d.complete_name || d.id; });
      const employee = allEmployees.find((e) => e.work_email === user?.email) || null;
      const employeeId = employee?.id;

      if (employee) {
        setSummary([
          { label: intl.formatMessage({ id: "ess.dashboard.dept" }), value: employee.department_id ? (deptMap[employee.department_id] || employee.department_id) : "—" },
          { label: intl.formatMessage({ id: "ess.dashboard.email" }), value: employee.work_email || "—" },
          { label: intl.formatMessage({ id: "ess.dashboard.phone" }), value: employee.work_phone || "—" },
        ]);
      } else {
        setSummary([]);
      }

      let statsData = [...statsFallback];
      let pending = [];

      const [leaveRes, annRes, attRes, slipRes] = await Promise.all([
        employeeId ? get(`/api/leave-requests?employee_id=${employeeId}`) : Promise.resolve({ data: [] }),
        get("/api/announcements"),
        employeeId ? get(`/api/attendance?employee_id=${employeeId}`) : Promise.resolve({ data: [] }),
        employeeId ? get(`/api/payslips?employee_id=${employeeId}`) : Promise.resolve({ data: [] }),
      ]);

      const leaves = leaveRes?.data || [];
      const leaveBalance = employee?.leave_balance != null ? employee.leave_balance : leaves.filter((l) => l.state === "validate" && l.leave_type === "annual").reduce((s, l) => s + (l.number_of_days || 0), 0);
      statsData[0] = {
        ...statsData[0],
        value: leaveBalance != null ? String(Math.round(leaveBalance)) : "—",
        sub: intl.formatMessage({ id: "ess.dashboard.leaveDays" }),
      };

      const attendances = attRes?.data || [];
      const todayCheckIns = attendances.filter((a) => {
        if (!a.check_in) return false;
        const d = new Date(a.check_in).toISOString().split("T")[0];
        const today = new Date().toISOString().split("T")[0];
        return d === today;
      });
      const todayAtt = todayCheckIns[0];
      let workedHours = null;
      if (todayAtt) {
        const ci = new Date(todayAtt.check_in).getTime();
        if (todayAtt.worked_hours != null) {
          workedHours = Number(todayAtt.worked_hours);
        } else if (todayAtt.check_out) {
          workedHours = (new Date(todayAtt.check_out).getTime() - ci) / 3600000;
        } else {
          workedHours = (Date.now() - ci) / 3600000;
        }
        if (!Number.isFinite(workedHours) || workedHours < 0) workedHours = null;
      }
      statsData[1] = {
        ...statsData[1],
        value: todayAtt ? fmtTime(todayAtt.check_in) : "—",
        sub: todayAtt
          ? intl.formatMessage({ id: "ess.dashboard.checkedIn" }, { hours: (workedHours ?? 0).toFixed(1) })
          : intl.formatMessage({ id: "ess.common.noData" }),
      };

      const payslips = slipRes?.data || [];
      const nextSlip = payslips.find((p) => {
        const d = new Date(p.date_from || p.date_to || p.create_date || 0);
        return d > new Date();
      });
      statsData[3] = {
        ...statsData[3],
        value: nextSlip ? (nextSlip.date_from || "—") : "—",
        sub: intl.formatMessage({ id: "ess.dashboard.nextPayroll" }),
      };

      if (employeeId) {
        const expRes = await get(`/api/expenses?employee_id=${employeeId}&state=draft&limit=50`);
        const expenses = expRes?.data || [];

        expenses.filter((e) => e.state === "draft" || e.state === "submitted").forEach((e) => {
          pending.push({
            icon: Send,
            title: e.name || intl.formatMessage({ id: "ess.dashboard.expenseFallback" }),
            sub: `${(e.amount || 0).toLocaleString()} XAF`,
            status: e.state === "draft"
              ? intl.formatMessage({ id: "ess.dashboard.statusDraft" })
              : intl.formatMessage({ id: "ess.dashboard.statusSubmitted" }),
            statusColor: "bg-amber-100 text-amber-700",
          });
        });
      }

      statsData[2] = {
        ...statsData[2],
        value: String(pending.length),
        sub: pending.length
          ? intl.formatMessage({ id: "ess.dashboard.actionCount" }, { count: pending.length })
          : intl.formatMessage({ id: "ess.dashboard.noAction" }),
      };

      if (pending.length === 0) {
        pending.push({
          icon: CheckCircle2,
          title: intl.formatMessage({ id: "ess.dashboard.allUpToDate" }),
          sub: intl.formatMessage({ id: "ess.dashboard.noPendingAction" }),
          status: intl.formatMessage({ id: "ess.dashboard.statusOk" }),
          statusColor: "bg-emerald-100 text-emerald-700",
        });
      }

      setPendingActions(pending);

      const calendarData = buildCalendar(attendances, CUR_YEAR, now.getMonth());
      setCalendar(calendarData);

      const realAnnouncements = (annRes?.data || []).filter((a) => a.state === "Published" || a.state === "published").slice(0, 5);
      if (realAnnouncements.length > 0) {
        setAnnouncements(realAnnouncements.map((a) => ({
          title: a.title || a.name || "—",
          sub: a.preview || a.subtitle || "",
          date: a.create_date ? new Date(a.create_date).toLocaleDateString(intl.locale, { month: 'short', day: 'numeric' }) : "",
          pinned: a.pinned || false,
        })));
      }
      setStats(statsData);
    } catch (_) {
      setSummary([]);
      setPendingActions([]);
      setCalendar([]);
    }
    setLoaded(true);
  }, [uid, intl, statsFallback, CUR_YEAR, now, user?.email, announcementsFallback]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!loaded) return <PageSkeleton />;

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {intl.formatMessage({ id: "ess.dashboard.title" })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {intl.formatMessage(
            { id: "ess.dashboard.subtitle" },
            {
              name: user?.username || intl.formatMessage({ id: "ess.common.user" }),
              date: `${CUR_MONTH} ${CUR_YEAR}`,
            }
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, sub, icon: Icon, bg, iconBg }) => (
          <div key={labelId} className={`rounded-xl border p-5 ${bg} transition-all hover:shadow-md`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  {intl.formatMessage({ id: labelId })}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-500 mt-1">{sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0 ${iconBg}`}>
                {typeof Icon === "function" || typeof Icon === "object" ? (
                  <Icon size={20} />
                ) : (
                  <span className="text-lg">{Icon}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            {intl.formatMessage({ id: "ess.dashboard.attendanceTitle" }, { month: CUR_MONTH, year: CUR_YEAR })}
          </h3>
          <div className="grid grid-cols-7 gap-1.5 text-center mb-2">
            {WEEKDAY_KEYS.map((key) => (
              <div key={key} className="text-[11px] font-semibold text-gray-400">
                {intl.formatMessage({ id: key })}
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            {calendar.map((row, i) => (
              <div key={i} className="grid grid-cols-7 gap-1.5">
                {row.map((cell, j) => {
                  if (cell.type === "empty") return <div key={j} className="h-9" />;
                  if (cell.type === "weekend") return <div key={j} className="h-9 rounded-md bg-gray-50 text-gray-400 text-xs font-semibold flex items-center justify-center">{cell.day}</div>;
                  if (cell.type === "present") {
                    return (
                      <div key={j} className={`h-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center ${cell.isToday ? "ring-2 ring-emerald-500 font-bold" : ""}`}>
                        {cell.isToday ? cell.day : <Check size={16} className="text-emerald-500 stroke-[2.5]" />}
                      </div>
                    );
                  }
                  if (cell.type === "late") {
                    return (
                      <div key={j} className={`h-9 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center ${cell.isToday ? "ring-2 ring-amber-500 font-bold" : ""}`}>
                        {cell.isToday ? cell.day : <Clock size={16} className="text-amber-500" />}
                      </div>
                    );
                  }
                  if (cell.type === "leave") {
                    return (
                      <div key={j} className="h-9 rounded-md bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                        {intl.formatMessage({ id: "ess.dashboard.leaveMarker" })}
                      </div>
                    );
                  }
                  if (cell.type === "today") return <div key={j} className="h-9 rounded-md bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center justify-center ring-2 ring-emerald-500">{cell.day}</div>;
                  if (cell.type === "future") return <div key={j} className="h-9 rounded-md bg-gray-50 text-gray-300 text-xs flex items-center justify-center">{cell.day}</div>;
                  if (cell.type === "absent") return <div key={j} className="h-9 rounded-md bg-red-50 text-red-400 text-xs font-semibold flex items-center justify-center">{cell.day}</div>;
                  return <div key={j} className="h-9 rounded-md bg-gray-50 text-gray-300 text-xs flex items-center justify-center">—</div>;
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-5 mt-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 rounded bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <Check size={11} className="stroke-[2.5]" />
              </span>
              {intl.formatMessage({ id: "ess.dashboard.legendPresent" })}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 rounded bg-amber-100 text-amber-600 flex items-center justify-center">
                <Clock size={11} />
              </span>
              {intl.formatMessage({ id: "ess.dashboard.legendLate" })}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 rounded bg-red-50 border border-red-200 text-red-400 flex items-center justify-center text-[10px] font-bold">
              </span>
              {intl.formatMessage({ id: "ess.dashboard.legendAbsent" })}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-violet-500" />
              {intl.formatMessage({ id: "ess.dashboard.legendLeave" })}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 rounded border-2 border-emerald-500 bg-emerald-50 text-emerald-600 flex items-center justify-center text-[10px] font-bold">
              </span>
              {intl.formatMessage({ id: "ess.dashboard.legendToday" })}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            {intl.formatMessage({ id: "ess.dashboard.quickSummary" })}
          </h3>
          <div className="divide-y divide-gray-100">
            {summary.map(({ label, value }, i) => (
              <div key={i} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>
                <span className="text-sm font-semibold text-gray-900">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            {intl.formatMessage({ id: "ess.dashboard.pendingActions" })}
          </h3>
          <div className="divide-y divide-gray-100">
            {pendingActions.map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-3.5 first:pt-0 last:pb-0">
                <div className="w-9 h-9 rounded-lg bg-gray-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  {typeof item.icon === "function" || typeof item.icon === "object" ? (
                    React.createElement(item.icon, { size: 16 })
                  ) : (
                    <span className="text-base">{item.icon}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800">{item.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${item.statusColor}`}>{item.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            {intl.formatMessage({ id: "ess.dashboard.announcements" })}
          </h3>
          <div className="divide-y divide-gray-100">
            {announcements.map((item, i) => (
              <div key={i} className="flex items-start justify-between gap-3 py-3.5 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900">
                    {item.pinned && <Megaphone size={14} className="inline text-rose-500 mr-1.5 -mt-0.5 rotate-[-10deg]" />}
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.sub || item.preview}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{item.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ESSDashboardPage;